/**
 * Avatar utilities for CRM objects.
 *
 * Provides functions for generating Gravatar URLs, favicon URLs,
 * and initials for CRM object avatars.
 */

import md5 from 'md5';

/**
 * Generate Gravatar URL from email address.
 *
 * @param email - Email address to hash
 * @param size - Image size in pixels
 * @returns Gravatar URL with 404 default (no image if not found)
 */
export function getGravatarUrl(email: string, size: number): string {
  const hash = md5(email.toLowerCase().trim());
  return `https://www.gravatar.com/avatar/${hash}?d=404&s=${size}`;
}

/**
 * Generate Google favicon service URL for a domain.
 *
 * @param domain - Domain name (e.g., "example.com")
 * @param size - Image size in pixels
 * @returns Google favicon service URL
 */
export function getFaviconUrl(domain: string, size: number): string {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=${size}`;
}

/**
 * Extract domain from email or URL.
 *
 * @param input - Email address, URL, or plain domain
 * @returns Domain string or null if extraction fails
 */
export function extractDomain(input: string): string | null {
  if (!input || input.trim() === '') {
    return null;
  }

  const trimmed = input.trim();

  // Check if it's an email
  if (trimmed.includes('@')) {
    const parts = trimmed.split('@');
    if (parts.length === 2 && parts[1].includes('.')) {
      return parts[1];
    }
    return null;
  }

  // Check if it's a URL (case-insensitive scheme check)
  const lowerTrimmed = trimmed.toLowerCase();
  if (
    lowerTrimmed.startsWith('http://') ||
    lowerTrimmed.startsWith('https://')
  ) {
    try {
      const url = new URL(trimmed);
      return url.hostname;
    } catch {
      return null;
    }
  }

  // Check if it looks like a domain (contains a dot)
  // Strip path/query/fragment for inputs like "example.com/contact"
  if (trimmed.includes('.') && !trimmed.includes(' ')) {
    const slashIndex = trimmed.search(/[/?#]/);
    const domainOnly =
      slashIndex === -1 ? trimmed : trimmed.slice(0, slashIndex);
    return domainOnly || null;
  }

  return null;
}

/**
 * Minimum natural pixel dimension for a favicon to be considered valid.
 * Google's favicon service returns a tiny default globe icon (≤16px)
 * for domains that don't have a real favicon. We reject images at or
 * below this threshold so the UI falls through to initials instead of
 * showing a pixelated globe that looks the same for every unknown domain.
 */
const MIN_FAVICON_SIZE = 16;

/**
 * Create an `onLoad` handler that rejects low-quality favicon images.
 *
 * Use this alongside `onError` on any `<img>` that loads a favicon URL.
 * If the image's natural dimensions are too small (Google default globe),
 * `onReject` is called so the component can fall through to its next
 * fallback (typically initials).
 *
 * @example
 * <img
 *   src={faviconUrl}
 *   onLoad={createFaviconLoadGuard(onError)}
 *   onError={onError}
 * />
 */
export function createFaviconLoadGuard(
  onReject: () => void
): (e: React.SyntheticEvent<HTMLImageElement>) => void {
  return (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (
      img.naturalWidth <= MIN_FAVICON_SIZE ||
      img.naturalHeight <= MIN_FAVICON_SIZE
    ) {
      onReject();
    }
  };
}

/**
 * Get avatar initials from a name string.
 *
 * @param name - Full name string
 * @returns 1-2 character initials string
 */
export function getAvatarInitials(name: string): string {
  if (!name || name.trim() === '') {
    return '';
  }

  const words = name
    .trim()
    .split(/\s+/)
    .filter(w => w.length > 0);

  if (words.length === 0) {
    return '';
  }

  if (words.length === 1) {
    // Single word: take first 2 characters
    return words[0].slice(0, 2).toUpperCase();
  }

  // Multiple words: take first letter of each word, up to 2 characters
  return words
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
